import React, { useState, useMemo } from 'react';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Import toast styles
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Paper,
  TablePagination,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './StudentReportTable.css';
import ContentHeader from '../../../common/ContentHeader/ContentHeader';
import StyledSelect from '../../../common/StyledComponents/StyledSelect';
import ArticleIcon from "@mui/icons-material/Article";
import useAxios from '../../../../hooks/useAxios';
import ReportModal from '../../../Reporting/Modals/ReportModal/ReportModal';
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: "var(--dark-purple)",
  color: "var(--light-gray)",
  textTransform: 'capitalize',
}));
const StyledTableCellSecondary = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    backgroundColor: "var(--light-gray)",
    color: "var(--dark-purple)",
    textTransform: 'capitalize',
  
}));

const StyledRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StudentReportTable = ({ data }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const axiosInstance = useAxios();

  const handleViewReportClick = async (sessionId) => {
    try {
      setLoadingReport(true);
      const response = await axiosInstance.get(`/sessions/${sessionId}/report`);
      if (response.data.success) {
        setCurrentReport(response.data.report);
        setReportModalOpen(true);
      } else {
        console.error(response.data.message || "Failed to fetch report.");
        toast.error(response.data.message || "Failed to fetch report.");
      }
    } catch (err) {
        toast.error("Error fetching session report:", err);
    } finally {
      setLoadingReport(false);
    }
  };


  // Handle expand/collapse
  const toggleExpandRow = (quizId) => {
    setExpandedRows((prev) => ({ ...prev, [quizId]: !prev[quizId] }));
  };

  // Filter data based on selected quiz
  const filteredData = useMemo(() => {
    if (!filterText) return data;
    return data.filter((quiz) => quiz.quiz_title === filterText);
  }, [data, filterText]);

  // Slice data for pagination
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
    setPage(0); // Reset to first page on filter change
  };

  return (
    <Box className="report-table-container">
      <Box className="report-table-controls" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {/* <Typography variant="h6" fontWeight="bold" color="primary">
          Quiz Reports
        </Typography> */}
        <ContentHeader
            title="Attended Assessments"
        />
        <StyledSelect
          value={filterText}
          onChange={handleFilterChange}
          displayEmpty
          variant="outlined"
          size="small"
          className="filter-select"
        >
          <MenuItem value="">
            <em>All Assessments</em>
          </MenuItem>
          {data.map((quiz) => (
            <MenuItem key={quiz.quiz_id} value={quiz.quiz_title}>
              {quiz.quiz_title}
            </MenuItem>
          ))}
        </StyledSelect>
      </Box>

      <Paper elevation={3} className="report-table-paper">
        <TableContainer>
          <Table aria-label="student report table">
            <TableHead className="table-header">
              <TableRow className="table-header">
                <StyledTableCell />
                <StyledTableCell>Assessment Title</StyledTableCell>
                <StyledTableCell align="center">Attempts</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((quiz) => (
                <React.Fragment key={quiz.quiz_id}>
                  <StyledRow>
                    <TableCell>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => toggleExpandRow(quiz.quiz_id)}
                      >
                        {expandedRows[quiz.quiz_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell className="capitalize-text">{quiz.quiz_title}</TableCell>
                    <TableCell align="center">
                      <Chip label={quiz.sessions.length} className="colored-chip" />
                    </TableCell>
                  </StyledRow>
                  <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={3}>
                      <Collapse in={expandedRows[quiz.quiz_id]} timeout="auto" unmountOnExit>
                        <Table size="small" aria-label="sessions">
                          <TableHead>
                            <TableRow>
                              <StyledTableCellSecondary>Attempt</StyledTableCellSecondary>
                              <StyledTableCellSecondary>Final Evaluation</StyledTableCellSecondary>
                              <StyledTableCellSecondary>Date</StyledTableCellSecondary>
                              <StyledTableCellSecondary>Actions</StyledTableCellSecondary>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {quiz.sessions.map((session) => (
                              <TableRow key={session.id}>
                                <TableCell>{session.attempt}</TableCell>
                                <TableCell>
                                  {session.final_evaluation !== null ? session.final_evaluation : 'N/A'}
                                </TableCell>
                                <TableCell>{session.created_at}</TableCell>
                                {/* Actions cell */}
                                <TableCell>
                                <Tooltip title={"AI Report"}>
                                  <IconButton onClick={() => handleViewReportClick(session.id)}>
                                      <ArticleIcon /> {/* or a suitable icon representing a report */}
                                  </IconButton>
                                </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {paginatedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No Assessments found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <ReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        report={currentReport}
      />
      <ToastContainer position="top-right" autoClose={5000} />  
    </Box>
    
  );
};

export default StudentReportTable;
